import { useRef, useState } from 'react';
import { NativeSelect } from '@mantine/core';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@components/breadcrumb';
import NextNav from '@components/next-nav';
import Map, {
  AttributionControl,
  Layer,
  NavigationControl,
  ScaleControl,
  FullscreenControl,
  Source,
  ViewStateChangeEvent,
  MapRef,
  ProjectionSpecification,
} from 'react-map-gl/mapbox';
import type { MapMouseEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { colorExpression } from '@lib/map';
import { LayerValue } from '@src/types/maps';
import Legend from './composite-map-legend-features';
import classes from './composite-map.module.css';

function CompositeMap() {
  const { clientId, tripProfileId } = useParams();
  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState({
    latitude: 49.03,
    longitude: 11.44,
    zoom: 3.5,
  });
  const layerColor = colorExpression(); // TODO: prevent this being called on re-renders
  const [layerValues, setLayerValues] = useState<Array<LayerValue>>();
  const [nutsName, setNutsName] = useState('');
  const [projection, setProjection] = useState('globe');

  const moveHandler = (changeEvent: ViewStateChangeEvent) => {
    // console.log('moeHandler Event: ', changeEvent);
    const currViewState = changeEvent.viewState;
    setViewState({
      latitude: currViewState.latitude,
      longitude: currViewState.longitude,
      zoom: currViewState.zoom,
    });
  };

  const clickHandler = (event: MapMouseEvent) => {
    // console.log('##', mapRef.current?.queryRenderedFeatures(event.point));
    const compositeMapFeature = mapRef.current?.queryRenderedFeatures(event.point, {
      layers: ['composite-map-layer'],
    })[0];
    // convert postgres array to array
    const layerIds: number[] = compositeMapFeature?.properties?.layers_array.match(/[\w.-]+/g).map(Number);
    const layersValues: string[] = compositeMapFeature?.properties?.values_array.match(/[\w.-]+/g).map(String);
    const layers = layerIds.map((id, index) => ({ id, value: layersValues[index] }));
    setLayerValues(layers);
    setNutsName(compositeMapFeature?.properties?.nuts_name);
  };

  return (
    <div>
      <Breadcrumb pb={30} pageStep={1} />
      <Map
        ref={mapRef}
        mapboxAccessToken={`${import.meta.env.MAPBOX_TOKEN}`}
        initialViewState={viewState}
        style={{ height: 700 }}
        projection={{ name: projection as ProjectionSpecification['name'] }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        attributionControl={false}
        onMove={moveHandler}
        onClick={clickHandler}
      >
        <NavigationControl />
        <ScaleControl />
        <FullscreenControl />
        <AttributionControl customAttribution="© Global Thematic Insights" />

        <Source
          id="composite-map"
          type="vector"
          url={`${import.meta.env.MVT_BASE_URL}/composite-map?trip_profile_id=${tripProfileId}`}
        >
          <Layer
            id="composite-map-layer"
            type="fill"
            source-layer="gis_composite_maps"
            paint={{
              'fill-color': layerColor,
              'fill-outline-color': 'black',
              'fill-opacity': 0.6,
            }}
          />
        </Source>
      </Map>

      <Legend layerValues={layerValues} nutsName={nutsName} />
      <div className={classes.mapBar}>
        Longitude: {viewState.longitude.toFixed(4)} | Latitude: {viewState.latitude.toFixed(4)} | Zoom:{' '}
        {viewState.zoom.toFixed(1)}
      </div>
      <NativeSelect
        w={200}
        label="Mapbox Projection"
        value={projection}
        data={[
          'albers',
          'equalEarth',
          'equirectangular',
          'lambertConformalConic',
          'mercator',
          'naturalEarth',
          'winkelTripel',
          'globe',
        ]}
        onChange={(event) => setProjection(event.currentTarget.value)}
      />

      <NextNav done={`/clients/${clientId}`} saveVisible={false} />
    </div>
  );
}

export default CompositeMap;
